import * as Highcharts from "highcharts"
import Formatters from "../formatters"
import { applyHighchartsTheme } from "../utils/highcharts_theme"

// Translation helper - looks up key in server-provided translations
function t(key) {
  return window.I18n?.[key] || key
}

// Get locale from server-provided I18n config
function getLocale() {
  return window.I18n?.numberFormat?.locale || "en"
}

// Date formatter - kept colocated as it's chart-specific
function createDateFormatter() {
  return new Intl.DateTimeFormat(getLocale(), {
    month: "long",
    year: "numeric",
  })
}

function lastMonthTime() {
  const date = new Date()
  date.setDate(1)
  date.setMonth(date.getMonth() - 1)
  return date.getTime()
}

function prepareData(data, valueKey) {
  return data.map((item) => {
    const month = new Date(item.month).getTime()

    return {
      x: month,
      y: parseFloat(item[valueKey]),
    }
  })
}

export const MonthlyActivityChart = {
  mounted() {
    this.data = JSON.parse(this.el.dataset.monthlyActivity)
    this.currencySymbol = this.el.dataset.currencySymbol || ""
    this.areaUnit = this.el.dataset.areaUnit || ""
    this.showChart()
  },

  updated() {
    this.data = JSON.parse(this.el.dataset.monthlyActivity)
    this.currencySymbol = this.el.dataset.currencySymbol || ""
    this.areaUnit = this.el.dataset.areaUnit || ""
    this.showChart()
  },

  showChart() {
    const lastMonth = lastMonthTime()
    const symbol = this.currencySymbol
    const unit = this.areaUnit
    const dateFormatter = createDateFormatter()

    const sizeData = prepareData(this.data, "size")
    const commissionData = prepareData(this.data, "commissions")

    applyHighchartsTheme(Highcharts)

    Highcharts.chart(this.el, {
      title: null,
      chart: {
        backgroundColor: "transparent",
      },
      plotOptions: {
        series: {
          zoneAxis: "x",
          zones: [{ value: lastMonth }, { dashStyle: "Dash" }],
        },
      },
      legend: {
        enabled: true,
        align: "center",
        verticalAlign: "top",
        floating: true,
        padding: 5,
        itemStyle: {
          fontWeight: "normal",
          fontSize: "14px",
        },
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        type: "datetime",
        lineWidth: 0,
        labels: {
          style: {
            fontSize: "12px",
          },
          formatter: function () {
            return new Intl.DateTimeFormat(getLocale(), {
              month: "short",
              year: "numeric",
            }).format(this.value)
          },
        },
      },
      yAxis: [
        {
          title: null,
          labels: {
            formatter: function () {
              return Formatters.area(this.value, { unit, compact: true })
            },
            style: {
              fontSize: "12px",
            },
          },
        },
        {
          title: null,
          labels: {
            formatter: function () {
              return Formatters.currency(this.value, { symbol, compact: true })
            },
            style: {
              fontSize: "12px",
            },
          },
          opposite: true,
        },
      ],
      series: [
        {
          name: t("Size"),
          data: sizeData,
          color: "#FF6600",
        },
        {
          name: t("Commissions"),
          data: commissionData,
          yAxis: 1,
          color: "#0066FF",
        },
      ],
      tooltip: {
        shared: true,
        crosshairs: true,
        style: {
          fontSize: "14px",
        },
        formatter: function () {
          return (
            dateFormatter.format(this.x) +
            "<br>" +
            this.points
              .map(
                (point) =>
                  `<span style="color:${point.series.color}">\u25CF</span> ${point.series.name}: <b>${
                    point.series.name === t("Commissions")
                      ? Formatters.currency(point.y, { symbol, decimals: 0 })
                      : Formatters.area(point.y, { unit })
                  }</b>`,
              )
              .join("<br>")
          )
        },
      },
    })
  },
}
